import {AlertTriangle, ChevronDown} from "lucide-react";
import {useEffect, useState} from "react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {cn} from "@/lib/utils";
import {useFormField} from "@/components/ui/form";
import {Button} from "@/components/ui/button";

import type {PropertyRuleTimeRange} from "@/src/modules/properties/types";

import type {SafelistingCoveragePreview} from "../lib/compute-safelisting-coverage-preview";
import {
  getCoverageAnchorHumanLabel,
  getSingleNightCoverageHeadline,
  isAnchorCoverageInProgress,
} from "../lib/coverage-anchor-hints";
import type {CoverageAnchorMismatch} from "../lib/coverage-anchor-hints";
import {SAFELISTING_COVERAGE_CALLOUT_CLASS} from "../lib/safelisting-coverage-callout";

/** Collapse per-night list for longer ranges to save vertical space in the sheet. */
const DETAIL_COLLAPSE_THRESHOLD = 4;

type SafelistingCoveragePreviewPanelProps = {
  preview: SafelistingCoveragePreview | null;
  rangeHours?: PropertyRuleTimeRange | null;
  timeZone?: string;
  mismatch?: CoverageAnchorMismatch | null;
  onUseLikelyAnchor?: () => void;
};

export function SafelistingCoveragePreviewPanel({
  preview,
  rangeHours,
  timeZone,
  mismatch,
  onUseLikelyAnchor,
}: SafelistingCoveragePreviewPanelProps) {
  const {formDescriptionId} = useFormField();
  const [detailsOpen, setDetailsOpen] = useState(true);

  useEffect(() => {
    if (!preview) return;
    setDetailsOpen(preview.rows.length < DETAIL_COLLAPSE_THRESHOLD);
  }, [preview]);

  if (!preview && !mismatch) return null;

  const countLabel = preview
    ? preview.permitCount === 1
      ? `1 ${preview.dateWord}`
      : `${preview.permitCount} ${preview.dateWord}s`
    : null;

  const singleNightHeadline =
    preview && preview.rows.length === 1 && rangeHours && timeZone
      ? getSingleNightCoverageHeadline(
          preview.rows[0]!.anchorDate,
          rangeHours,
          preview.rows[0]!.compactTimeLabel,
          timeZone,
        )
      : null;

  const headlineCoverage = preview
    ? singleNightHeadline != null
      ? singleNightHeadline
      : (preview.overallCompactLabel ?? preview.overallRangeLabel)
    : null;

  const showCollapsibleDetails = (preview?.rows.length ?? 0) >= DETAIL_COLLAPSE_THRESHOLD;
  const showPerNightBreakdown = (preview?.rows.length ?? 0) > 1;
  const breakdownScope = preview?.dateWord === "night" ? "nightly" : "daily";

  const nightList = preview ? (
    <ul className="m-0 flex list-none flex-col gap-1 p-0" aria-label="Coverage by date">
      {preview.rows.map((row) => {
        const inProgress =
          rangeHours && timeZone
            ? isAnchorCoverageInProgress(row.anchorDate, rangeHours, timeZone)
            : false;
        const humanLabel =
          rangeHours && timeZone
            ? getCoverageAnchorHumanLabel(row.anchorDate, rangeHours, timeZone)
            : null;

        return (
          <li
            key={row.anchorDate}
            className="text-muted-foreground flex flex-wrap items-baseline gap-x-1.5 gap-y-0 text-sm leading-snug tabular-nums"
          >
            {inProgress ? (
              <span className="text-foreground font-semibold">
                {getSingleNightCoverageHeadline(
                  row.anchorDate,
                  rangeHours!,
                  row.compactTimeLabel,
                  timeZone!,
                )}
              </span>
            ) : humanLabel ? (
              <>
                <span className="text-foreground font-semibold">{humanLabel}</span>
                <span>{row.compactTimeLabel}</span>
              </>
            ) : (
              <>
                <span className="text-foreground min-w-[3.25rem] font-medium">
                  {row.compactDateLabel}
                </span>
                <span>{row.compactTimeLabel}</span>
              </>
            )}
          </li>
        );
      })}
    </ul>
  ) : null;

  return (
    <div
      role="status"
      aria-live="polite"
      id={`${formDescriptionId}-coverage`}
      className={cn(SAFELISTING_COVERAGE_CALLOUT_CLASS)}
    >
      {preview && !mismatch ? (
        <>
          <div className="flex flex-wrap items-baseline gap-x-1.5">
            <span className="text-muted-foreground text-xs font-medium">
              Covers
            </span>
            {headlineCoverage ? (
              <span className="text-foreground text-sm font-medium tabular-nums leading-snug">
                {headlineCoverage}
              </span>
            ) : null}
            {countLabel ? (
              <span className="text-muted-foreground text-xs tabular-nums">· {countLabel}</span>
            ) : null}
          </div>

          {showPerNightBreakdown ? (
            <div className="flex flex-col gap-1 pt-0.5">
              {showCollapsibleDetails ? (
                <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
                  <CollapsibleTrigger
                    type="button"
                    className="text-muted-foreground hover:text-foreground flex w-full items-center justify-between gap-2 text-left text-xs font-medium"
                  >
                    <span>
                      {detailsOpen
                        ? `Each ${preview.dateWord} (${preview.rows.length})`
                        : `Show ${breakdownScope} breakdown`}
                    </span>
                    <ChevronDown
                      className={cn(
                        "size-3.5 shrink-0 transition-transform",
                        detailsOpen && "rotate-180",
                      )}
                      aria-hidden
                    />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-1.5">{nightList}</CollapsibleContent>
                </Collapsible>
              ) : (
                <>
                  <span className="text-muted-foreground text-xs font-medium">
                    Each {preview.dateWord}
                  </span>
                  {nightList}
                </>
              )}
            </div>
          ) : null}

          {preview.windowHint && preview.rows.length > 1 ? (
            <p className="text-muted-foreground text-xs leading-snug">{preview.windowHint}</p>
          ) : null}
        </>
      ) : null}

      {mismatch ? (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-0.5">
          <span className="text-muted-foreground inline-flex items-center gap-1 text-xs leading-snug">
            <AlertTriangle className="size-3.5 shrink-0" aria-hidden />
            <span>
              Did you mean{" "}
              <span className="text-foreground font-medium">{mismatch.likelyAnchorLabel}</span>?
              {mismatch.likelyCoverageLine ? (
                <>
                  {" "}
                  <span className="tabular-nums">{mismatch.likelyCoverageLine}</span>
                </>
              ) : null}
            </span>
          </span>
          {onUseLikelyAnchor ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs font-semibold text-amber-800 underline-offset-2 hover:text-amber-900"
              onClick={onUseLikelyAnchor}
            >
              Use {mismatch.likelyAnchorLabel} instead
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
